import {
  Input, InputRef, Tag, Tooltip, message,
} from 'antd';
import React, {
  ChangeEvent,
  FC,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CloseCircleFilled } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { notification } from '../../../antdProvider';
import styles from './styles.module.less';
import { Icon } from '..';

type TagInputProps = {
  className?: string
  style?: React.CSSProperties
  searchTags: string[]
  searchText: string
  addonAfter: JSX.Element | null
  onChangeTags: (value: string[]) => void
  onChangeText: (value: string) => void
};

const TagInput: FC<TagInputProps> = ({
  searchTags = [],
  searchText = '',
  addonAfter,
  onChangeText,
  onChangeTags,
  ...props
}) => {
  const inputRef = useRef<InputRef>(null);
  const [textValue, setTextValue] = useState(searchText);
  const trimmedTextValue = textValue.trim();

  const debouncedOnChangeText = useMemo(() => debounce((t: string) => onChangeText(t), 400), []);

  const handleTagDelete = (tag: string) => {
    const newArr = searchTags.filter(i => i !== tag);
    onChangeTags(newArr);
    message.destroy();
  };

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setTextValue(text);
    debouncedOnChangeText(text);
  };

  const handleTextSubmit = () => {
    debouncedOnChangeText.cancel();
    if (!trimmedTextValue) return;

    if (searchTags.includes(trimmedTextValue)) {
      notification.error({
        message: `${trimmedTextValue} already exists`,
      });
      return;
    }

    if (trimmedTextValue !== '') {
      onChangeTags([...searchTags, trimmedTextValue]);
    }

    setTextValue('');
    onChangeText('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); }
    if (e.key === 'Backspace' && !textValue) {
      onChangeTags(searchTags.slice(0, -1));
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleTextSubmit();
    }
  };

  const handleClear = () => {
    debouncedOnChangeText.cancel();
    onChangeTags([]);
    setTextValue('');
    onChangeText('');
  };

  return (
    <div className={`${styles.inputFilter}`}>
      <div
        className={`
          ${styles.tagInput}
          ${searchTags.length && addonAfter ? styles.tagInputSquared : styles.tagInputRounded}`}
        onClick={() => inputRef.current?.focus()}
        {...props}
        role="button"
        tabIndex={0}
      >
        <div className={styles.iconContainer}>
          <Icon name="search" />
        </div>
        {searchTags.map(item => (item.length > 20 ? (
          <Tooltip title={item} key={item}>
            <Tag
              style={{ fontWeight: 'bold' }}
              closable
              onClose={() => handleTagDelete(item)}
            >
              {`${item.slice(0, 20)}...`}
            </Tag>
          </Tooltip>
        ) : (
          <Tag
            key={item}
            style={{ fontWeight: 'bold' }}
            closable
            onClose={() => handleTagDelete(item)}
          >
            {item}
          </Tag>
        )))}
        <Input
          ref={inputRef}
          variant="borderless"
          placeholder="Search"
          value={textValue}
          onChange={handleTextChange}
          onBlur={handleTextSubmit}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          style={{ paddingLeft: 0 }}
        />
        {searchTags.length || textValue ? (
          <Tooltip title="clear">
            <div
              onClick={handleClear}
              role="button"
              tabIndex={0}
            >
              <CloseCircleFilled style={{ color: '#00000073', fontSize: 12, paddingRight: 12 }} />
            </div>
          </Tooltip>
        ) : null}
      </div>
      {searchTags.length > 1 ? addonAfter : null}
    </div>
  );
};

export default TagInput;
